// src/Pages/AddProduct.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import '../Style/AddProduct.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaCloudUploadAlt, FaSpinner, FaTimes } from 'react-icons/fa';

import {
  collection, getDocs, addDoc, serverTimestamp, doc, getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebaseConfig';

const MAX_IMG_MB = 10;
const MAX_FILE_MB = 25;

// One accept list that includes BOTH images + files
const UPLOAD_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.zip,.rar,.7z,image/*';

const pad4 = n => String(n).padStart(4, '0');
const todayISO = () => new Date().toISOString().slice(0, 10);

// helper to clamp numeric ranges safely
const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

// Simple “blocklist” for risky executable/script types
const isBlockedAttachment = (file) => {
  const name = (file?.name || '').toLowerCase().trim();
  const blockedExt = ['.exe', '.msi', '.apk', '.bat', '.cmd', '.com', '.scr', '.js', '.vbs', '.ps1', '.sh'];
  return blockedExt.some(ext => name.endsWith(ext));
};

const safeFilename = (name) => (name || 'file').replace(/[^\w.\-() ]+/g, '_');
const uidPart = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function AddProduct() {
  const navigate = useNavigate();

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ONE upload input for images + files
  // Each item: { file: File, previewUrl?: string }
  const [uploads, setUploads] = useState([]);
  const uploadInputRef = useRef(null);
  const [uploadDragActive, setUploadDragActive] = useState(false);

  // Core form state
  const [productId, setProductId] = useState('Loading…');           // non-editable human code
  const [inHousePart, setInHousePart] = useState('');               // NYA0001 …
  const [name, setName] = useState('');                             // Stock item name
  const [description, setDescription] = useState('');
  const [dateCreated, setDateCreated] = useState(todayISO());
  const [oemPart, setOemPart] = useState('');

  // Inventory & pricing
  const [quantity, setQuantity] = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [oemPrice, setOemPrice] = useState(''); // ✅ NEW (OEM Price)
  const [markupPct, setMarkupPct] = useState(''); // %
  const [discountPct, setDiscountPct] = useState(''); // %

  // Validation
  const [errors, setErrors] = useState({
    name: false,
    inHousePart: false,
    dateCreated: false,
    quantity: false,
    costPrice: false
  });

  // Derived pricing & totals
  const derived = useMemo(() => {
    const qty = parseFloat(quantity) || 0;
    const cost = parseFloat(costPrice) || 0;

    const mRaw = parseFloat(markupPct) || 0;
    const dRaw = parseFloat(discountPct) || 0;

    const m = clamp(mRaw, 0, 100000);
    const d = clamp(dRaw, 0, 100);

    const unitBefore = cost;
    const unitAfter = cost * (1 + m / 100);
    const unitAfterDiscount = unitAfter * (1 - d / 100);

    const totalBefore = unitBefore * qty;
    const totalAfter = unitAfter * qty;
    const totalAfterDiscount = unitAfterDiscount * qty;

    const fix = (n) => Number((Number.isFinite(n) ? n : 0).toFixed(2));

    return {
      unitBefore: fix(unitBefore),
      unitAfter: fix(unitAfter),
      unitAfterDiscount: fix(unitAfterDiscount),

      totalBefore: fix(totalBefore),
      totalAfter: fix(totalAfter),
      totalAfterDiscount: fix(totalAfterDiscount),

      normalizedMarkupPct: fix(m),
      normalizedDiscountPct: fix(d),
    };
  }, [quantity, costPrice, markupPct, discountPct]);

  // Seed productId and NYA sequence based on counts
  useEffect(() => {
    const init = async (user) => {
      if (!user) {
        toast.error('Please login to add a product.', { className: 'toast-error' });
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const snap = await getDocs(collection(db, 'Products'));
        const count = snap.size;
        setProductId(`PROD${count + 1}`);
        setInHousePart(`NYA${pad4(count + 1)}`);
      } catch (err) {
        console.error(err);
        toast.error('Failed to prepare product form.', { className: 'toast-error' });
      } finally {
        setLoading(false);
      }
    };
    const unsub = auth.onAuthStateChanged(init);
    return () => unsub();
  }, []);

  // --- Unified upload helpers (images + files) ---
  const pickUploads = () => uploadInputRef.current?.click();

  const addUploads = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const next = [];

    for (const f of files) {
      if (!f) continue;

      if (isBlockedAttachment(f)) {
        toast.error(`Blocked file type: ${f.name}`, { className: 'toast-error' });
        continue;
      }

      const isImg = (f.type || '').startsWith('image/');
      const limitMb = isImg ? MAX_IMG_MB : MAX_FILE_MB;

      if (f.size > limitMb * 1024 * 1024) {
        toast.error(`"${f.name}" exceeds ${limitMb} MB.`, { className: 'toast-error' });
        continue;
      }

      // prevent duplicates (simple: name+size)
      const exists = uploads.some(u => u.file?.name === f.name && u.file?.size === f.size);
      if (exists) continue;

      const previewUrl = isImg ? URL.createObjectURL(f) : '';
      next.push({ file: f, previewUrl });
    }

    if (next.length) {
      setUploads(prev => [...prev, ...next]);
    }
  };

  const onUploadInput = (e) => {
    addUploads(e.target.files);
    e.target.value = ''; // allow re-select same file later
  };

  const onUploadDragOver = (e) => { e.preventDefault(); setUploadDragActive(true); };
  const onUploadDragLeave = () => setUploadDragActive(false);
  const onUploadDrop = (e) => {
    e.preventDefault();
    setUploadDragActive(false);
    addUploads(e.dataTransfer.files);
  };

  const removeUploadAt = (idx) => {
    setUploads(prev => {
      const item = prev[idx];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearUploads = () => {
    setUploads(prev => {
      prev.forEach(u => { if (u?.previewUrl) URL.revokeObjectURL(u.previewUrl); });
      return [];
    });
  };

  // cleanup previews on unmount
  useEffect(() => {
    return () => {
      uploads.forEach(u => { if (u?.previewUrl) URL.revokeObjectURL(u.previewUrl); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pick a “primary” image preview for UI + imageUrl field
  const primaryImagePreview = useMemo(() => {
    const img = uploads.find(u => (u.file?.type || '').startsWith('image/'));
    return img?.previewUrl || null;
  }, [uploads]);

  const validate = useCallback(() => {
    const newErr = {
      name: !name.trim(),
      inHousePart: !inHousePart.trim(),
      dateCreated: !dateCreated,
      quantity: !(parseFloat(quantity) >= 0),
      costPrice: !(parseFloat(costPrice) >= 0),
    };
    setErrors(newErr);
    return !Object.values(newErr).some(Boolean);
  }, [name, inHousePart, dateCreated, quantity, costPrice]);

  // Upload ALL selected items (images + files) to storage
  const uploadAllIfAny = async () => {
    if (!uploads.length) return { uploaded: [], primaryImageUrl: '' };

    const results = await Promise.all(
      uploads.map(async (u) => {
        const f = u.file;
        const storedAs = `${uidPart()}_${safeFilename(f.name)}`;
        const storageRef = ref(storage, `Products/${productId}/Uploads/${storedAs}`);

        await uploadBytes(storageRef, f, { contentType: f.type || 'application/octet-stream' });
        const url = await getDownloadURL(storageRef);

        return {
          name: f.name,
          storedAs,
          url,
          size: f.size,
          type: f.type || '',
          kind: (f.type || '').startsWith('image/') ? 'image' : 'file',
        };
      })
    );

    const primary = results.find(r => r.kind === 'image')?.url || '';
    return { uploaded: results, primaryImageUrl: primary };
  };

  const clearForm = () => {
    setName('');
    setDescription('');
    setDateCreated(todayISO());
    setOemPart('');
    setQuantity('');
    setCostPrice('');
    setOemPrice(''); // ✅ NEW
    setMarkupPct('');
    setDiscountPct('');
    clearUploads();
    setErrors({ name: false, inHousePart: false, dateCreated: false, quantity: false, costPrice: false });
  };

  const saveProduct = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please correct highlighted fields.', { className: 'toast-error' });
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      toast.error('Please login first.', { className: 'toast-error' });
      return;
    }

    try {
      setSaving(true);

      const uSnap = await getDoc(doc(db, 'users', user.uid));
      const { firstName = '', lastName = '' } = uSnap.exists() ? uSnap.data() : {};

      const { uploaded, primaryImageUrl } = await uploadAllIfAny();

      await addDoc(collection(db, 'Products'), {
        productId,
        inHousePart: inHousePart.trim(),
        name: name.trim(),
        description: description.trim() || '—',
        dateCreated,
        oemPart: oemPart.trim() || '—',

        quantity: quantity === '' ? 0 : Number(parseFloat(quantity).toFixed(2)),
        costPrice: costPrice === '' ? 0 : Number(parseFloat(costPrice).toFixed(2)),

        // ✅ NEW
        oemPrice: oemPrice === '' ? 0 : Number(parseFloat(oemPrice).toFixed(2)),

        markupPct: markupPct === '' ? 0 : Number(parseFloat(markupPct).toFixed(2)),
        discountPct: discountPct === '' ? 0 : Number(parseFloat(discountPct).toFixed(2)),

        unitBeforeProfit: derived.unitBefore,
        unitAfterProfit: derived.unitAfter,
        unitAfterDiscount: derived.unitAfterDiscount,
        sellingPrice: derived.unitAfterDiscount,

        totalBeforeProfit: derived.totalBefore,
        totalAfterProfit: derived.totalAfter,
        totalAfterDiscount: derived.totalAfterDiscount,

        // Backwards-compatible: keep imageUrl as first uploaded image (if any)
        imageUrl: primaryImageUrl || '',

        // Store all uploads (images + files) together
        files: uploaded, // [{name, storedAs, url, size, type, kind}]

        authId: user.uid,
        createdBy: `${firstName} ${lastName}`.trim(),
        createdAt: serverTimestamp(),
      });

      toast.success('Product saved successfully!', { className: 'toast-success' });

      // Prepare next suggested codes
      setProductId(prev => {
        const n = parseInt(prev.replace(/\D/g, ''), 10) + 1 || 1;
        return `PROD${n}`;
      });
      setInHousePart(prev => {
        const n = (parseInt(prev.replace(/\D/g, ''), 10) + 1) || 1;
        return `NYA${pad4(n)}`;
      });

      clearForm();
    } catch (err) {
      console.error(err);
      toast.error('Could not save product.', { className: 'toast-error' });
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => navigate('/products');

  if (loading) {
    return (
      <div className="addproduct-page">
        <Sidebar />
        <div className="addproduct-loading">
          <FaSpinner className="spin" size={48} />
          <p>Preparing product form…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="addproduct-page">
      <ToastContainer
        position="top-right"
        autoClose={2600}
        toastClassName={({ type }) => (type === 'success' ? 'toast-success' : 'toast-error')}
      />
      <Sidebar />

      <main className="addproduct-main">
        <div className="title-row">
          <h2>Add Product</h2>
          <span className="badge-id">{productId}</span>
        </div>

        <form className="addproduct-form" onSubmit={saveProduct}>
          {/* Product Details */}
          <section className="card">
            <h3>Product Details</h3>

            <div className="grid-5">
              <div className="field">
                <label>Product ID</label>
                <input value={productId} readOnly title="Auto-generated product code" />
              </div>

              <div className="field">
                <label>In-House Part No. <span className="required">*</span></label>
                <input
                  placeholder="NYA0001"
                  value={inHousePart}
                  onChange={e => setInHousePart(e.target.value)}
                  className={errors.inHousePart ? 'input-error' : ''}
                />
                {errors.inHousePart && <span className="error-text">Required</span>}
              </div>

              <div className="field">
                <label>Stock Item Name <span className="required">*</span></label>
                <input
                  placeholder="Enter product name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className={errors.name ? 'input-error' : ''}
                />
                {errors.name && <span className="error-text">Required</span>}
              </div>

              <div className="field">
                <label>Date Created <span className="required">*</span></label>
                <input
                  type="date"
                  value={dateCreated}
                  onChange={e => setDateCreated(e.target.value)}
                  className={errors.dateCreated ? 'input-error' : ''}
                />
                {errors.dateCreated && <span className="error-text">Required</span>}
              </div>

              <div className="field">
                <label>OEM Part No.</label>
                <input
                  placeholder="Enter OEM part number"
                  value={oemPart}
                  onChange={e => setOemPart(e.target.value)}
                />
              </div>
            </div>

            <div className="field mt">
              <label>Description</label>
              <textarea
                rows={3}
                placeholder="Describe the product"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
          </section>

          {/* Pricing & Inventory */}
          <section className="card">
            <div className="sec-head">
              <h3>Pricing & Inventory</h3>
            </div>

            {/* OEM Price added here (it will wrap nicely if grid-5 is fixed in CSS) */}
            <div className="grid-5">
              <div className="field">
                <label>Quantity <span className="required">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={quantity}
                  onChange={e => setQuantity(e.target.value)}
                  className={errors.quantity ? 'input-error' : ''}
                />
                {errors.quantity && <span className="error-text">Required</span>}
              </div>

              <div className="field">
                <label>Cost Price <span className="required">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={costPrice}
                  onChange={e => setCostPrice(e.target.value)}
                  className={errors.costPrice ? 'input-error' : ''}
                />
                {errors.costPrice && <span className="error-text">Required</span>}
              </div>

              {/* ✅ NEW OEM Price */}
              <div className="field">
                <label>OEM Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={oemPrice}
                  onChange={e => setOemPrice(e.target.value)}
                  title="Optional: price from OEM / supplier"
                />
              </div>

              <div className="field">
                <label>Mark-up %</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 25"
                  value={markupPct}
                  onChange={e => setMarkupPct(e.target.value)}
                />
              </div>

              <div className="field">
                <label>Discount %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="e.g. 10"
                  value={discountPct}
                  onChange={e => setDiscountPct(e.target.value)}
                  title="Applies to the marked-up selling price"
                />
              </div>

              <div className="field">
                <label>Selling Price (Auto)</label>
                <input
                  readOnly
                  value={Number.isFinite(derived.unitAfterDiscount) ? derived.unitAfterDiscount.toFixed(2) : '0.00'}
                  title="Auto-calculated: Cost Price × (1 + Mark-up % / 100) × (1 - Discount % / 100)"
                />
              </div>
            </div>

            <div className="totals-bar">
              <div className="tot-item">
                <span className="lbl">Unit (Before Profit)</span>
                <span className="val">R{derived.unitBefore.toFixed(2)}</span>
              </div>
              <div className="tot-item">
                <span className="lbl">Unit (After Profit)</span>
                <span className="val">R{derived.unitAfter.toFixed(2)}</span>
              </div>
              <div className="tot-item">
                <span className="lbl">Unit (After Discount)</span>
                <span className="val">R{derived.unitAfterDiscount.toFixed(2)}</span>
              </div>

              <div className="tot-item">
                <span className="lbl">Total (Before Profit)</span>
                <span className="val">R{derived.totalBefore.toFixed(2)}</span>
              </div>
              <div className="tot-item">
                <span className="lbl">Total (After Profit)</span>
                <span className="val">R{derived.totalAfter.toFixed(2)}</span>
              </div>
              <div className="tot-item">
                <span className="lbl">Total (After Discount)</span>
                <span className="val strong">R{derived.totalAfterDiscount.toFixed(2)}</span>
              </div>
            </div>
          </section>

          {/* ONE unified uploader for Images + Files */}
          <section className="card">
            <h3>Product Uploads</h3>
            <p className="hint">
              Upload images and/or files together. Images max {MAX_IMG_MB} MB each. Files max {MAX_FILE_MB} MB each.
            </p>

            <div
              className={`upload-area ${uploadDragActive ? 'drag' : ''}`}
              onClick={pickUploads}
              onDragOver={onUploadDragOver}
              onDragLeave={onUploadDragLeave}
              onDrop={onUploadDrop}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') pickUploads(); }}
            >
              <FaCloudUploadAlt size={34} />
              <p>{uploadDragActive ? 'Drop to add' : 'Click or drag uploads here'}</p>
              <input
                ref={uploadInputRef}
                type="file"
                accept={UPLOAD_ACCEPT}
                multiple
                hidden
                onChange={onUploadInput}
              />
            </div>

            {/* Primary image preview (first image selected) */}
            {primaryImagePreview && (
              <div className="image-preview-wrap">
                <div className="image-preview">
                  <img src={primaryImagePreview} alt="Preview" />
                </div>
              </div>
            )}

            {/* Selected uploads list (images + files) */}
            {uploads.length > 0 && (
              <div className="mt">
                <div className="hint" style={{ marginBottom: 10 }}>
                  Selected: <strong>{uploads.length}</strong>
                </div>

                <div className="file-list">
                  {uploads.map((u, idx) => {
                    const f = u.file;
                    const isImg = (f.type || '').startsWith('image/');
                    return (
                      <div key={`${f.name}-${f.size}-${idx}`} className="file-row">
                        <div className="file-meta">
                          <div className="file-name" title={f.name}>
                            {isImg ? '🖼️ ' : '📄 '}
                            {f.name}
                          </div>
                          <div className="file-sub">
                            {(f.size / (1024 * 1024)).toFixed(2)} MB {f.type ? `• ${f.type}` : ''}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn-outline"
                          onClick={() => removeUploadAt(idx)}
                          title="Remove"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                        >
                          <FaTimes />
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* Actions */}
          <section className="card action-card">
            <div className="actions-row">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save Product'}
              </button>
              <button type="button" className="btn-outline" onClick={clearForm} disabled={saving}>
                Clear Form
              </button>
              <button type="button" className="btn-outline" onClick={cancel} disabled={saving}>
                Cancel
              </button>
            </div>
          </section>
        </form>
      </main>

      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={58} />
          <p>Saving…</p>
        </div>
      )}
    </div>
  );
}
